import React, { useState, useEffect } from 'react';
// Added Grid for responsive hooks
import { Table, Tag, Input, Card, Typography, Divider, List, Empty, Descriptions, Space, Row, Col, Grid } from 'antd';
import { SearchOutlined, RocketOutlined, IdcardOutlined } from '@ant-design/icons';
import api from '../../api/axios';
import moment from 'moment';

const { Text, Title } = Typography;
const { useBreakpoint } = Grid; // RESPONSIVE CHANGE

const UserManagement = () => {
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const screens = useBreakpoint(); // RESPONSIVE CHANGE
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
        total: 0,
    });
    const [searchText, setSearchText] = useState('');

    const fetchUsers = async (page = 1, search = '') => {
        setLoading(true);
        try {
            const response = await api.get(`/admin/users?page=${page}&search=${search}`);
            setData(response.data.data); 
            setPagination({
                current: response.data.current_page,
                pageSize: response.data.per_page,
                total: response.data.total,
            });
        } catch (error) {
            console.error('Failed to load users:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers(pagination.current, searchText);
    }, []);

    const handleTableChange = (newPagination) => {
        fetchUsers(newPagination.current, searchText);
    };

    const handleSearch = (value) => {
        setSearchText(value);
        fetchUsers(1, value); 
    };

    // --- EXPANDABLE ROW CONTENT ---
    const expandedRowRender = (record) => {
        return (
            // RESPONSIVE CHANGE: Less padding on mobile
            <div style={{ padding: screens.xs ? '5px 10px' : '10px 20px', backgroundColor: '#fdfdfd', borderRadius: '8px', border: '1px solid #f0f0f0' }}>
                <Row gutter={[24, 24]}>
                    {/* Column 1: Personal Details */}
                    {/* RESPONSIVE CHANGE: span={24} on mobile, 12 on desktop */}
                    <Col xs={24} md={12}>
                        <Divider orientation="left"><IdcardOutlined /> Additional Details</Divider>
                        <Descriptions column={1} size="small">
                            <Descriptions.Item label="DOB">
                                {record.dob ? moment(record.dob).format('LL') : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Phone">{record.phone_number || 'N/A'}</Descriptions.Item>
                            <Descriptions.Item label="Bio">
                                <Text type="secondary">{record.bio || 'No bio provided.'}</Text>
                            </Descriptions.Item>
                        </Descriptions>
                    </Col>

                    {/* Column 2: Goals */}
                    {/* RESPONSIVE CHANGE: span={24} on mobile, 12 on desktop */}
                    <Col xs={24} md={12}>
                        {record.role === 'mentee' ? (
                            <>
                            <Divider orientation="left"><RocketOutlined /> Mentee Goals</Divider>
                            <List
                                size="small"
                                dataSource={record.commitments || []}
                                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No goals set yet." /> }}
                                renderItem={(goal) => (
                                    <List.Item style={{ padding: '8px 0' }}>
                                        <Card size="small" style={{ width: '100%', borderLeft: '3px solid #FE1F08' }}>
                                            <Text strong>{goal.title}</Text>
                                            <div style={{ marginTop: 5 }}>
                                                {goal.milestones?.map(m => (
                                                    <Tag color="default" key={m.id} style={{ fontSize: '10px', marginBottom: '4px' }}>{m.title}</Tag>
                                                ))}
                                            </div>
                                        </Card>
                                    </List.Item>
                                )}
                            />
                            </>
                        ) : (
                            <div style={{ padding: '20px 0' }}>
                                <Text type="secondary" italic>This user is a Mentor. Mentors do not set learning goals.</Text>
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        );
    };

    const columns = [
        { 
            title: 'Name', 
            dataIndex: 'name', 
            key: 'name',
            fixed: screens.xs ? 'left' : false, // RESPONSIVE CHANGE: Keep name visible while scrolling right
        },
        // RESPONSIVE CHANGE: Hide Email and Profession on mobile to avoid overcrowding
        // These can be viewed in the expanded row if added there, or via horizontal scroll
        ...(screens.xs ? [] : [
            { title: 'Email', dataIndex: 'email', key: 'email' },
            { title: 'Profession', dataIndex: 'profession', key: 'profession' },
        ]),
        { 
            title: 'Role', 
            dataIndex: 'role', 
            render: (role) => (
                <Tag color={role === 'mentor' ? 'blue' : 'green'}>
                    {screens.xs ? role[0].toUpperCase() : role.toUpperCase()}
                </Tag>
            )
        },
    ];

    return (
        <Card 
            title="Users" 
            // RESPONSIVE CHANGE: Move search bar below title on mobile
            extra={!screens.xs && (
                <Input.Search
                    placeholder="Search..."
                    onSearch={handleSearch}
                    style={{ width: 250 }}
                    allowClear
                />
            )}
        >
            {/* RESPONSIVE CHANGE: Show search bar full width on mobile */}
            {screens.xs && (
                <div style={{ marginBottom: 16 }}>
                    <Input.Search
                        placeholder="Search users..."
                        onSearch={handleSearch}
                        allowClear
                        style={{ width: '100%' }}
                    />
                </div>
            )}

            <Table 
                columns={columns} 
                dataSource={data} 
                rowKey="id" 
                loading={loading}
                pagination={pagination}
                onChange={handleTableChange}
                // RESPONSIVE CHANGE: Essential for mobile tables
                scroll={{ x: 'max-content' }}
                expandable={{
                    expandedRowRender,
                    rowExpandable: (record) => record.role !== 'admin',
                    // RESPONSIVE CHANGE: Ensure the expand icon is always accessible
                    fixed: true 
                }}
            />
        </Card>
    );
};

export default UserManagement;